import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  serverTimestamp,
  Timestamp,
  updateDoc,
  where,
} from 'firebase/firestore';
import { db } from '../firebase/firebase-config';
import { COLLECTIONS } from '../platform/constants';
import { LAST_READ_MODULES } from '../platform/lastRead/lastReadModules';
import { markItemAsRead } from '../platform/lastRead/lastReadService';
import {
  isPinnedActive,
  parsePinnedUntil,
  sortPinnedItems,
} from '../platform/pinnedContent';
import { withTenantScope } from './contentWriteFields';
import { deleteContentAttachmentStorage } from './attachmentUploadService';
import { syncAttachmentFields } from '../platform/contentAttachments';
import {
  applyTenantScopeToCollection,
  buildCreatedAtListQuery,
  canViewAllUnitsForTenant,
  mapAndFilterSnapshot,
} from './tenantListQuery';
import { getVisibleUnitIds } from './contentQueryHelpers';

export const BENEFITS_PAGE_SIZE = 20;
export const PINNED_BENEFITS_LIMIT = 5;

function benefitsCollection() {
  return collection(db, COLLECTIONS.BENEFITS);
}

function mapBenefit(docSnap) {
  const data = docSnap.data();
  return {
    id: docSnap.id,
    ...data,
    createdAt: data.createdAt?.toDate?.() ?? null,
    pinnedUntil: parsePinnedUntil(data.pinnedUntil),
    pinPriority: data.pinPriority ?? 0,
  };
}

function isBenefitVisibleToUser(user, memberships, tenantId, benefit) {
  if (!user) return false;
  if (canViewAllUnitsForTenant(user, memberships, tenantId)) {
    return true;
  }
  const visibleUnits = getVisibleUnitIds(user);
  return visibleUnits.includes(benefit.unit);
}

function buildBenefitsListQuery(user, {
  tenantId,
  memberships,
  pageSize = BENEFITS_PAGE_SIZE,
  lastVisible = null,
} = {}) {
  return buildCreatedAtListQuery({
    collectionName: COLLECTIONS.BENEFITS,
    tenantId,
    user,
    memberships,
    pageSize,
    lastVisible,
  });
}

export function buildPinnedBenefitsQuery(tenantId) {
  const scoped = applyTenantScopeToCollection(COLLECTIONS.BENEFITS, tenantId);
  return query(
    scoped,
    where('pinnedUntil', '>', Timestamp.now()),
    orderBy('pinnedUntil', 'asc'),
    limit(PINNED_BENEFITS_LIMIT),
  );
}

export function buildBenefitsPageQuery(user, tenantId, memberships, options = {}) {
  return buildBenefitsListQuery(user, {
    tenantId,
    memberships,
    pageSize: options.pageSize ?? BENEFITS_PAGE_SIZE,
    lastVisible: options.startAfterDoc ?? null,
  });
}

export async function fetchActivePinnedBenefits(user, tenantId, memberships) {
  if (!tenantId) {
    return [];
  }

  const pinnedQuery = buildPinnedBenefitsQuery(tenantId);
  const querySnapshot = await getDocs(pinnedQuery);
  const pinned = mapAndFilterSnapshot(querySnapshot, tenantId, mapBenefit)
    .filter((benefit) => isPinnedActive(benefit))
    .filter((benefit) => isBenefitVisibleToUser(user, memberships, tenantId, benefit));

  return sortPinnedItems(pinned);
}

export async function countActivePinnedBenefits(tenantId, excludeBenefitId = null) {
  if (!tenantId) {
    return 0;
  }

  const pinnedQuery = buildPinnedBenefitsQuery(tenantId);
  const querySnapshot = await getDocs(pinnedQuery);
  return querySnapshot.docs
    .map(mapBenefit)
    .filter((benefit) => isPinnedActive(benefit))
    .filter((benefit) => benefit.id !== excludeBenefitId)
    .length;
}

export async function fetchBenefitsPage(user, tenantId, memberships, options = {}) {
  const listQuery = buildBenefitsPageQuery(user, tenantId, memberships, options);
  const querySnapshot = await getDocs(listQuery);
  return {
    benefits: mapAndFilterSnapshot(querySnapshot, tenantId, mapBenefit),
    lastVisible: querySnapshot.docs[querySnapshot.docs.length - 1] || null,
  };
}

export async function createBenefit(activeTenantId, payload) {
  const attachmentFields = payload.attachments
    ? syncAttachmentFields(payload.attachments)
    : syncAttachmentFields(payload.fileUrl ? [{ url: payload.fileUrl }] : []);

  const pinFields = {};
  if (payload.pinnedUntil) {
    pinFields.pinnedUntil = payload.pinnedUntil instanceof Date
      ? Timestamp.fromDate(payload.pinnedUntil)
      : payload.pinnedUntil;
    pinFields.pinPriority = payload.pinPriority ?? 0;
  } else {
    pinFields.pinnedUntil = null;
    pinFields.pinPriority = 0;
  }

  const docRef = await addDoc(
    benefitsCollection(),
    withTenantScope(activeTenantId, {
      ...payload,
      ...attachmentFields,
      ...pinFields,
      createdAt: serverTimestamp(),
    }),
  );
  return docRef.id;
}

export async function updateBenefit(benefitId, payload) {
  const nextPayload = { ...payload };
  if (payload.attachments) {
    Object.assign(nextPayload, syncAttachmentFields(payload.attachments));
  }

  if ('pinnedUntil' in payload) {
    nextPayload.pinnedUntil = payload.pinnedUntil
      ? (payload.pinnedUntil instanceof Date
        ? Timestamp.fromDate(payload.pinnedUntil)
        : payload.pinnedUntil)
      : null;
    nextPayload.pinPriority = payload.pinnedUntil ? (payload.pinPriority ?? 0) : 0;
  }

  await updateDoc(doc(db, COLLECTIONS.BENEFITS, benefitId), nextPayload);
}

export async function deleteBenefit(benefitId) {
  const benefitRef = doc(db, COLLECTIONS.BENEFITS, benefitId);
  const benefitSnap = await getDoc(benefitRef);
  if (benefitSnap.exists()) {
    await deleteContentAttachmentStorage(benefitSnap.data(), {
      extraUrls: [benefitSnap.data()?.thumbnailUrl],
    });
  }
  await deleteDoc(benefitRef);
}

export async function markBenefitAsRead(userId, benefitId) {
  await markItemAsRead(userId, LAST_READ_MODULES.BENEFITS, benefitId);
}

export { mapBenefit };
